/* ═══════════════════════════════════════════════════════════════════════════
   Sondage des capacités machine — étage 1 du système de qualité (PLAN.md §7.3).

   Ce que ce module ne fait PAS, et pourquoi :

   1. AUCUN SNIFFING DE GPU. `WEBGL_debug_renderer_info` est déprécié dans
      Firefox, Gecko renvoie des chaînes assainies, et Blink comme WebKit
      renvoient des chaînes CODÉES EN DUR, identiques pour tous les
      utilisateurs. Une détection bâtie dessus classe donc tout le monde dans la
      même case tout en donnant l'illusion de mesurer quelque chose — c'est pire
      que de ne rien détecter, parce qu'on lui fait confiance.

   2. AUCUN MICRO-BENCHMARK BLOQUANT. Le plan en prévoyait un de 400 ms pendant
      l'écran de chargement ; il n'est délibérément pas ici. Le budget de
      démarrage est déjà pris par le warm-up des shaders, et un benchmark de
      boot mesure le pire instant possible : GPU encore en montée de fréquence,
      shaders en cours de compilation, et sur mobile un appareil FROID — donc à
      son maximum, juste avant le throttling thermique qu'on cherche justement à
      anticiper. Le raffinement se fait après coup et passivement, via
      `refinePreset`, qui n'est pas appelée au démarrage.

   Reste un faisceau d'indices faibles mais gratuits.

   ⚠️ HONNÊTETÉ SUR CE QUE VALENT CES INDICES. `hardwareConcurrency` compte des
   threads CPU et ne dit STRICTEMENT RIEN du GPU, qui est le goulot de ce jeu :
   un portable à 16 threads avec un iGPU d'entrée de gamme le dépassera en
   score, un MacBook Air M1 à 8 threads rendra beaucoup mieux. `deviceMemory`
   est arrondi à une puissance de deux, plafonné à 8 par la spécification, et
   n'existe pas hors Chromium. Le seul signal réellement discriminant est
   `pointer: coarse`, qui sépare le mobile du poste fixe — et encore, il classe
   un iPad Pro avec un téléphone d'entrée de gamme.

   Le résultat est donc une HYPOTHÈSE DE DÉPART, volontairement conservatrice.
   Elle ne doit jamais servir à un choix irréversible (résolution des assets,
   flipbooks chargés) ; c'est le resolution scale continu, lui seul mesuré sur
   le rendu réel, qui corrige en cours de session (§7.3, étage 2).
   ═══════════════════════════════════════════════════════════════════════════ */

export type Preset = 'bas' | 'moyen' | 'haut';

/** Ordre des presets, exposé pour que l'appelant — et les tests — puissent les
 *  comparer sans réimplémenter la relation. */
export const PRESET_RANK: Readonly<Record<Preset, number>> = { bas: 0, moyen: 1, haut: 2 };

export interface Capabilities {
  /** Threads CPU annoncés. Voir l'avertissement en tête de module : ce n'est
   *  pas une mesure du GPU. */
  readonly cores: number;
  /** Gio annoncés par `navigator.deviceMemory`, `null` hors Chromium. La
   *  distinction « inconnu » / « faible » compte : elle est traitée comme
   *  neutre, pas comme une mauvaise nouvelle. */
  readonly deviceMemoryGb: number | null;
  readonly devicePixelRatio: number;
  readonly webgl2: boolean;
  readonly maxTextureSize: number;
  /** Préférence système `prefers-reduced-motion`. ⚠️ N'entre PAS dans le choix
   *  du preset : c'est une demande d'ACCESSIBILITÉ sur le mouvement (secousse
   *  de caméra, flashs, parallaxe), pas un indice de puissance. Les confondre
   *  dégraderait le rendu de quelqu'un qui a seulement demandé moins de
   *  mouvement, et laisserait la secousse active sur une machine faible. */
  readonly reducedMotion: boolean;
  /** `pointer: coarse` — tactile, donc mobile ou tablette dans la quasi-totalité
   *  des cas. */
  readonly coarsePointer: boolean;
}

/** Le strict nécessaire d'un `THREE.WebGLRenderer`. Structurel : `src/platform/`
 *  est la couche de base et n'importe ni three ni `src/view/` (§3.1). */
export interface RendererProbe {
  readonly capabilities?: { readonly isWebGL2?: boolean; readonly maxTextureSize?: number };
  getContext?(): unknown;
}

/* Valeurs de repli. Elles sont choisies NEUTRES et non pessimistes : un repli
   pessimiste ferait basculer en « bas » tout navigateur qui n'expose pas
   l'information, ce qui punirait Safari pour une API absente plutôt que pour
   une machine lente. */
const FALLBACK_CORES = 4;
const FALLBACK_MAX_TEXTURE_SIZE = 4096;

export function probeCapabilities(renderer?: RendererProbe | null): Capabilities {
  const gl = readGl(renderer);
  return Object.freeze({
    cores: readCores(),
    deviceMemoryGb: readDeviceMemory(),
    devicePixelRatio: readDpr(),
    webgl2: readWebgl2(renderer, gl),
    maxTextureSize: readMaxTextureSize(renderer, gl),
    reducedMotion: matchesMedia('(prefers-reduced-motion: reduce)'),
    coarsePointer: matchesMedia('(pointer: coarse)'),
  });
}

/* ── Le barème ────────────────────────────────────────────────────────────

   Un SCORE plutôt qu'une cascade de `if`, pour une raison précise : il rend la
   MONOTONIE structurelle. Améliorer une caractéristique ne peut jamais faire
   descendre de preset, parce que chaque contribution est positive et que les
   seuils sont croissants. Une cascade de conditions imbriquées perd cette
   propriété au troisième ajout sans que personne ne le remarque, et on se
   retrouve avec une machine à 16 cœurs classée sous une machine à 8.

   Répartition, et ce que chaque ligne prétend savoir :

     cœurs        0..2  — proxy grossier de la gamme de la machine ;
     mémoire      0..2  — inconnu vaut 1, soit exactement « ni bon ni mauvais » ;
     texture      0..1  — 8192+ indique un GPU non famélique, rien de plus ;
     pointeur     −2    — pénalité mobile.

   Maximum atteignable : 5. Le seuil « haut » est fixé À 5, ce qui a une
   conséquence assumée et vérifiée par les tests : AUCUN APPAREIL TACTILE NE
   PEUT ATTEINDRE « HAUT ». C'est voulu. Le budget de frame réserve 4,7 ms de
   marge au throttling thermique (§7.1) : un téléphone qui tient 60 fps pendant
   quinze secondes s'effondre à la troisième minute, et un preset optimiste ne
   se rattrape pas — les presets ne remontent jamais en cours de run.

   Le prix de ce choix, et il est réel : un flagship récent est classé « moyen »
   alors qu'il tiendrait « haut ». On préfère cette erreur-là à l'inverse, parce
   qu'elle est invisible pour le joueur (il voit un jeu fluide un peu moins
   détaillé) tandis que l'autre est un jeu qui saccade. */
const CORES_HIGH = 8;
const CORES_MID = 4;
const MEMORY_HIGH_GB = 8;
const MEMORY_MID_GB = 4;
const TEXTURE_BONUS_AT = 8192;
/** En dessous, on n'est pas devant une machine à ménager mais devant un GPU qui
 *  ne tiendra pas les flipbooks. Plancher dur, pas une pénalité de score. */
const TEXTURE_FLOOR = 4096;
const COARSE_PENALTY = 2;
const SCORE_FOR_HIGH = 5;
const SCORE_FOR_MEDIUM = 2;

export function suggestPreset(caps: Capabilities): Preset {
  /* Planchers durs, évalués avant le barème. Sans WebGL2, three r185 ne rend
     rien de ce que ce jeu suppose — il n'y a pas de version dégradée à
     proposer, seulement le preset le plus prudent. */
  if (!caps.webgl2) return 'bas';
  if (caps.maxTextureSize < TEXTURE_FLOOR) return 'bas';

  let score = 0;

  if (caps.cores >= CORES_HIGH) score += 2;
  else if (caps.cores >= CORES_MID) score += 1;

  const mem = caps.deviceMemoryGb;
  if (mem === null) score += 1;
  else if (mem >= MEMORY_HIGH_GB) score += 2;
  else if (mem >= MEMORY_MID_GB) score += 1;

  if (caps.maxTextureSize >= TEXTURE_BONUS_AT) score += 1;

  if (caps.coarsePointer) score -= COARSE_PENALTY;

  if (score >= SCORE_FOR_HIGH) return 'haut';
  if (score >= SCORE_FOR_MEDIUM) return 'moyen';
  return 'bas';
}

/** Ce que `refinePreset` a besoin de lire. `FrameStats` le satisfait déjà —
 *  interface structurelle plutôt qu'import, pour que les tests puissent fournir
 *  une distribution choisie sans fabriquer 240 échantillons. */
export interface FrameStatsView {
  percentile(p: number): number;
  readonly filledEnough: boolean;
}

/** Au-dessus de cette médiane de TRAVAIL, la machine ne tient pas le budget.
 *  12 ms est le budget de travail réel du plan (§7.1) ; on laisse 2 ms de jeu
 *  avant de conclure, pour ne pas rétrograder sur une frontière. */
const DOWNGRADE_P50_MS = 14;

/**
 * Raffinement PASSIF du preset, à partir de mesures déjà collectées.
 *
 * ⚠️ CETTE FONCTION N'EST PAS APPELÉE AU DÉMARRAGE, et ne doit pas l'être. Elle
 * ne mesure rien elle-même : elle relit un `FrameStats` que la boucle de rendu
 * alimente de toute façon. Le coût au boot est donc nul, ce qui est tout
 * l'intérêt par rapport au benchmark de 400 ms écarté plus haut.
 *
 * Deux conditions avant de rétrograder, chacune contre une erreur précise :
 *
 *   - L'ÉCHANTILLON DOIT ÊTRE SUFFISANT. Rétrograder sur douze frames revient à
 *     juger la machine pendant la compilation des shaders.
 *
 *   - LE RESOLUTION SCALE DOIT DÉJÀ ÊTRE AU PLANCHER. C'est le levier le plus
 *     rentable et il agit sans recompiler un seul shader (§7.2). Changer de
 *     preset alors qu'il reste de la marge dessus, c'est payer un stutter de
 *     recompilation pour un gain qu'on pouvait obtenir gratuitement.
 *
 * Elle ne remonte JAMAIS : §7.3 interdit toute remontée de preset en cours de
 * run, et l'appelant ne doit l'invoquer qu'à une transition d'écran, jamais
 * pendant un boss.
 */
export function refinePreset(
  current: Preset,
  stats: FrameStatsView,
  resolutionScale: number,
  scaleFloor = 0.5,
): Preset {
  if (current === 'bas') return 'bas';
  if (!stats.filledEnough) return current;
  if (resolutionScale > scaleFloor) return current;
  if (stats.percentile(0.5) <= DOWNGRADE_P50_MS) return current;
  return current === 'haut' ? 'moyen' : 'bas';
}

/* ── Lectures tolérantes ──────────────────────────────────────────────────
   Chaque source peut manquer (Safari, contexte de test, navigateur durci) ou
   mentir sur son type. Aucune de ces lectures ne doit pouvoir empêcher le jeu
   de démarrer : une capacité inconnue vaut son repli neutre, jamais une
   exception. */

function readCores(): number {
  try {
    if (typeof navigator === 'undefined') return FALLBACK_CORES;
    const n = navigator.hardwareConcurrency;
    return typeof n === 'number' && n > 0 ? Math.floor(n) : FALLBACK_CORES;
  } catch {
    return FALLBACK_CORES;
  }
}

function readDeviceMemory(): number | null {
  try {
    if (typeof navigator === 'undefined') return null;
    // Absente des types DOM : l'API n'est implémentée que par Chromium.
    const n = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
    return typeof n === 'number' && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function readDpr(): number {
  try {
    if (typeof window === 'undefined') return 1;
    const r = window.devicePixelRatio;
    return typeof r === 'number' && r > 0 ? r : 1;
  } catch {
    return 1;
  }
}

function matchesMedia(query: string): boolean {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches === true;
  } catch {
    return false;
  }
}

/** Contexte GL du renderer, s'il veut bien le donner. */
function readGl(renderer?: RendererProbe | null): GlProbe | null {
  try {
    if (!renderer || typeof renderer.getContext !== 'function') return null;
    const ctx = renderer.getContext();
    if (typeof ctx !== 'object' || ctx === null) return null;
    return ctx as GlProbe;
  } catch {
    return null;
  }
}

interface GlProbe {
  getParameter?(pname: number): unknown;
  MAX_TEXTURE_SIZE?: number;
  texStorage2D?: unknown;
}

function readWebgl2(renderer: RendererProbe | null | undefined, gl: GlProbe | null): boolean {
  // 1. Ce que three a déjà déterminé, quand on nous l'a passé.
  const flag = renderer?.capabilities?.isWebGL2;
  if (typeof flag === 'boolean') return flag;

  // 2. Le contexte lui-même. `texStorage2D` n'existe qu'en WebGL2 ; le tester
  //    évite de dépendre du global `WebGL2RenderingContext`, absent des
  //    environnements de test.
  if (gl && typeof gl.texStorage2D === 'function') return true;

  // 3. Faute de mieux, la simple présence de l'API dans le navigateur. Cela dit
  //    que la plateforme la connaît, pas qu'un contexte a pu être obtenu — d'où
  //    le rang de dernier recours.
  return typeof WebGL2RenderingContext !== 'undefined';
}

function readMaxTextureSize(
  renderer: RendererProbe | null | undefined,
  gl: GlProbe | null,
): number {
  const fromThree = renderer?.capabilities?.maxTextureSize;
  if (typeof fromThree === 'number' && fromThree > 0) return fromThree;

  try {
    if (gl && typeof gl.getParameter === 'function' && typeof gl.MAX_TEXTURE_SIZE === 'number') {
      const v = gl.getParameter(gl.MAX_TEXTURE_SIZE);
      if (typeof v === 'number' && v > 0) return v;
    }
  } catch {
    /* pilote peu coopératif : on retombe sur le repli neutre */
  }

  return FALLBACK_MAX_TEXTURE_SIZE;
}
